/**
 * Shared Analytics Types
 *
 * Common types used across analytics event definitions.
 */

/**
 * AI Models supported by DramaDetector
 */
export type AIModel =
  | 'llama-3.3-70b'
  | 'gpt-4o'
  | 'gpt-5.2'
  | 'claude-sonnet-4'
  | 'claude-sonnet-4.6'
  | 'claude-opus-4.6'
  | 'gemini-2.5-pro';

/**
 * Platform types
 */
export type Platform = 'ios' | 'android' | 'web';

/**
 * User subscription tiers
 *
 * DramaDetector tiers:
 * - free: Basic tier (10 analyses/day)
 * - drama_plus: Premium tier (500 analyses/day for family, 100 individual)
 * - dramax: Ultimate tier (1000 analyses/day for family, 200 individual)
 * - anonymous: Trial users (3 analyses per device)
 */
export type UserTier = 'free' | 'drama_plus' | 'dramax' | 'anonymous';

/**
 * User subscription tiers (without anonymous)
 */
export type AuthenticatedUserTier = 'free' | 'drama_plus' | 'dramax';

/**
 * Family plan tiers
 */
export type FamilyTier = 'drama_plus' | 'dramax';

/**
 * LLM Provider types
 */
export type LLMProvider = 'groq' | 'openai' | 'anthropic' | 'google';

/**
 * Billing intervals
 */
export type BillingInterval = 'month' | 'year';

/**
 * Gift card durations (in months)
 *
 * NOTE: Only used in lib/analytics/events/subscription.ts — could be inlined there if this file grows.
 */
export type GiftCardDuration = 3 | 6 | 12;

/**
 * Loading state variants (Executive Producer experiment)
 */
export type LoadingStateVariant = 'control' | 'inline' | 'modal';

/**
 * Share methods
 */
export type ShareMethod = 'link' | 'social' | 'email';

/**
 * Authentication methods
 */
export type AuthMethod = 'google' | 'email' | 'apple';

/**
 * Product types for checkout
 */
export type ProductType = 'subscription' | 'gift_card';

/**
 * Gated content types (for blur teaser analytics)
 *
 * Used to identify what type of premium content triggered paywall.
 * @see paywall.ts for conversion funnel events
 */
export type ContentType =
  | 'patterns' // Pattern Spotlight cards
  | 'behavioral_insights' // Behavioral context/trend analysis
  | 'micro_lessons' // Educational micro-lesson cards
  | 'archetype' // Archetype report/classification
  | 'annotations' // Inline annotation details
  | 'transformation' // Transformation insight cards (DramaX)
  | 'advanced_analysis' // Advanced analysis features
  | 'score_delta' // Score Delta indicator (gamification)
  | 'feature_preview' // Drama+ feature preview (DD-157 conversion UX)
  | 'style_consistency' // Style consistency insight (DD-1999)
  | 'ai_detection'; // Communication pattern insight (DD-2000)
